<template>
  <div class="ecircular-template">
    <div class="row ecircular-template__field">
      <div class="column small-12">
        <div class="row ecircular-template__label ">
          <label class="font-bold column small-10 align-self-bottom">
            {{ $t('settings.department.eCircularTemplate.defaultHeader') }}
            <span class="font-normal tip__text">({{ headerPlaceholder }})</span>
          </label>
          <div class="column small-2">
            <div class="row align-right">
              <button
                class="btn btn--orange"
                @click="openPreview"
              >
                {{ $t('action.preview') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="column small-12">
        <TinymceEditor
          :key="editorKey"
          v-model="headerTemplate"
          :disabled="!allowEdit"
          :height="editorHeight"
          :additionalToolbar="additionalToolbar"
        />
      </div>
    </div>
    <div class="row ecircular-template__field">
      <label class="ecircular-template__label font-bold column small-12">
        {{ $t('settings.department.eCircularTemplate.defaultFooter') }}
        <span class="font-normal tip__text">({{ footerPlaceholder }})</span>
      </label>
      <div class="column small-12">
        <TinymceEditor
          :key="editorKey"
          v-model="footerTemplate"
          :disabled="!allowEdit"
          :height="editorHeight"
          :additionalToolbar="additionalToolbar"
        />
      </div>
    </div>
    <div class="row ecircular-template__field">
      <label class="ecircular-template__label column small-12">
        <span class="font-bold">{{ $t('settings.department.eCircularTemplate.logo') }}</span>
        <span class="font-normal">{{ $t('settings.department.eCircularTemplate.logoTips_0') }}</span>
        <p class="font-normal tip__text">({{ $t('settings.department.eCircularTemplate.logoTips_1') }})</p>
      </label>
      <div class="column small-12">
        <HkldDropzone
          v-if="allowEdit"
          :sourceId="departmentId"
          :metaData="callCenterMetadata"
          :maxNumberOfFiles="1"
          uploadDocTypes="image/png,image/jpeg"
        />
        <EditAttachments
          ref="logoEditAttach"
          :sourceId="departmentId"
          :canEdit="allowEdit"
          :canDelete="allowEdit"
          :canDownloadAll="false"
          :filesFilterFn="filterECircularCallCenter"
        />
      </div>
    </div>
    <Modal
      ref="previewModal"
      modalType="large-modal"
    >
      <ECircularPreview
        :dataSrc="previewData"
      />
    </Modal>
  </div>
</template>

<script>
import _ from 'lodash'

import EventBus from '@/utils/eventBus'

import HkldDropzone from '@/controls/HkldDropzone'
import EditAttachments from '@/controls/EditAttachments'
import TinymceEditor from '@/controls/TinymceEditor'
import Modal from '@/controls/Modal'
import ECircularPreview from '../../../modules/eCircular/ECircularViews/ECircularPreview'
import { createTemplates, getTemplatesBySourceId, updateRawTemplates } from '@/api/settings/templates.request'
import { templateType } from '@/utils/consts'
import { ECIRCULAR_TEMPLATE } from '../department.consts'
import { getRandomIdSuffix } from '@/utils/vueCompHelper'

export default {
  name: 'ECircularTemplate',
  components: {
    HkldDropzone,
    EditAttachments,
    TinymceEditor,
    Modal,
    ECircularPreview,
  },
  model: {
    prop: 'extData',
    event: 'onChange',
  },
  props: {
    extData: {
      type: Object,
      default: undefined,
    },
    formOptions: {
      type: Object,
      default: undefined,
    },
  },
  data () {
    return {
      randomKey: getRandomIdSuffix(),
      editorHeight: 150,
      templates: [],
      pendingTemplates: {},
      previewData: {},
      mustHaveTemplateTypes: [templateType.ECIRCULAR_TENANT_DEPARTMENT_HEADER, templateType.ECIRCULAR_TENANT_DEPARTMENT_FOOTER],
      callCenterMetadata: {
        type: ECIRCULAR_TEMPLATE.ECIRCULAR_TENANT_DEPARTMENT_CALL_CENTER,
      },
    }
  },
  computed: {
    additionalToolbar () {
      if (this.allowEdit) {
        return ['hklBulletBList hklNumberedList', 'fontselect']
      } else {
        return ['hklBulletBList hklNumberedList']
      }
    },
    departmentId () {
      return this.extData.id || ''
    },
    editorKey () {
      return `${this.departmentId}_${this.randomKey}`
    },
    allowEdit () {
      const { formOptions: options } = this
      return !options.readonly && (
        options.isAdmin ||
        options.hasSettingUpdateAccess
      )
    },
    uploadSuccessEventName () {
      return `controls.hkldDropzone.newItem.${this.extData.id}`
    },
    headerPlaceholder () {
      return this.$t('settings.department.eCircularTemplate.defaultHeaderPlaceholder')
    },
    footerPlaceholder () {
      return this.$t('settings.department.eCircularTemplate.defaultFooterPlaceholder')
    },
    headerTemplate: {
      get: function () {
        return this.getTemplateContent(templateType.ECIRCULAR_TENANT_DEPARTMENT_HEADER)
      },
      set: function (value) {
        this.updatePendingTemplates(value, templateType.ECIRCULAR_TENANT_DEPARTMENT_HEADER)
      },
    },
    footerTemplate: {
      get: function () {
        return this.getTemplateContent(templateType.ECIRCULAR_TENANT_DEPARTMENT_FOOTER)
      },
      set: function (value) {
        this.updatePendingTemplates(value, templateType.ECIRCULAR_TENANT_DEPARTMENT_FOOTER)
      },
    },
  },
  watch: {
    'extData.id': {
      handler: async function switchDepartment (value, oldValue) {
        await this.initDepartmentTemplates()
        if (!_.isEmpty(value)) {
          EventBus.$off(`controls.hkldDropzone.newItem.${oldValue}`, this.uploadLogoCompletely)
          EventBus.$on(this.uploadSuccessEventName, this.uploadLogoCompletely)
        }
      },
      immediate: true,
    },
    allowEdit: {
      handler: function (newV, oldV) {
        if (newV) {
          this.pendingTemplates = Object.values(this.templates)
            .reduce((obj, rs) => {
              obj[rs.id] = rs
              return obj
            }, {})
        }
      },
      immediate: true,
    },
    async formOptions (val, pre) {
      if (val.readonly !== pre.readonly) {
        this.initDepartmentTemplates()
      }
    },
  },
  beforeDestroy () {
    EventBus.$off(this.uploadSuccessEventName, this.uploadLogoCompletely)
  },
  methods: {
    filterECircularCallCenter (files) {
      const result = files.filter(item => item.metaData.type === ECIRCULAR_TEMPLATE.ECIRCULAR_TENANT_DEPARTMENT_CALL_CENTER)
      return result
    },
    getPreviewTemplate (type) {
      const result = this.getTemplateContent(type, this.pendingTemplates)
      return result || this.getTemplateContent(type)
    },
    getTemplateContent (type, templates = this.templates) {
      const rs = _.find(templates, item => item.type === type)
      return _.defaultTo(_.get(rs, 'content'), '')
    },
    buildTemplateData (type, content = '') {
      return {
        type,
        content,
        sourceId: this.departmentId,
      }
    },
    updatePendingTemplates (value, type) {
      const rs = this.templates.find(item => item.type === type)
      if (rs && rs.id) {
        this.pendingTemplates[rs.id] = {
          ...rs,
          content: value,
        }
      } else {
        this.pendingTemplates[type] = this.buildTemplateData(type, value)
      }
    },
    uploadLogoCompletely (item) {
      const refLogoEditAttach = this.$refs.logoEditAttach
      const files = refLogoEditAttach.files
      if (files.length <= 1) { return }
      const oldLogo = _.get(files, [0]) || null
      try {
        refLogoEditAttach.deleteFile(oldLogo)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async initDepartmentTemplates () {
      if (_.isEmpty(this.departmentId)) return
      const result = await getTemplatesBySourceId(this.departmentId)
      this.templates = result.filter(item => this.mustHaveTemplateTypes.includes(item.type))
      this.pendingTemplates = {}
      this.randomKey = getRandomIdSuffix()
    },
    paddingMissingTemplates (templates) {
      const buildTemplateData = (type) => this.buildTemplateData(type, '')
      switch (templates.length) {
        case 0:
          return this.mustHaveTemplateTypes.map(buildTemplateData)
        case this.mustHaveTemplateTypes.length:
          return templates
        default:
          const hadTemplateType = templates.map(item => item.type)
          const missingTemplates = this.mustHaveTemplateTypes.filter(type => !hadTemplateType.includes(type)).map(buildTemplateData)
          return templates.concat(missingTemplates)
      }
    },
    async save () {
      const pendingTemplates = Object.values(this.pendingTemplates)
      if (_.isEmpty(this.templates)) {
        const newTemplates = this.paddingMissingTemplates(pendingTemplates)
        await createTemplates(newTemplates)
      } else {
        if (!_.isEmpty(pendingTemplates)) await updateRawTemplates(pendingTemplates)
      }
      await this.initDepartmentTemplates()
    },
    async openPreview () {
      const callCenterImgId = _.get(this.$refs, ['logoEditAttach', 'files', 0, 'id'], null)
      this.previewData = {
        header: this.getPreviewTemplate(templateType.ECIRCULAR_TENANT_DEPARTMENT_HEADER),
        callCenterImgId,
        description: '',
        footer: this.getPreviewTemplate(templateType.ECIRCULAR_TENANT_DEPARTMENT_FOOTER),
        signature: '',
      }
      this.$refs.previewModal.open()
    },
  },
}
</script>

<style lang="scss" scoped>
.tip__text {
  font-size: 12px;
  margin-bottom:0
}
.ecircular-template {
  &__field {
    margin-bottom:5px;
  }
  &__label {
    margin-bottom:5px;
  }
}
</style>
